import { useState, useEffect } from 'react';
import { DrawdownBatchService } from '@/services/drawdowns/drawdownBatchService';
import { DrawdownService } from '@/services/drawdowns/drawdownService';
import type {
  AvailableInvestor,
  ExistingInvestor,
  DrawdownDates,
  DrawdownInfo,
} from '../types';

interface UseDrawdownRecalculationDataProps {
  open: boolean;
  fundId: string;
  drawdownId: string;
}

export function useDrawdownRecalculationData({
  open,
  fundId,
  drawdownId,
}: UseDrawdownRecalculationDataProps) {
  const [loadingInvestors, setLoadingInvestors] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableInvestors, setAvailableInvestors] = useState<
    AvailableInvestor[]
  >([]);
  const [existingInvestors, setExistingInvestors] = useState<
    ExistingInvestor[]
  >([]);
  const [drawdownDates, setDrawdownDates] = useState<DrawdownDates | null>(
    null
  );
  const [drawdownInfo, setDrawdownInfo] = useState<DrawdownInfo | null>(null);
  const [dueDate, setDueDate] = useState<string>('');

  // Fetch available investors and existing batches when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!open || !fundId || !drawdownId) return;

      setLoadingInvestors(true);
      setLoadingBatches(true);
      setError(null);
      setAvailableInvestors([]);
      setExistingInvestors([]);
      setDrawdownInfo(null);
      setDrawdownDates(null);
      setDueDate('');

      try {
        // Fetch drawdown basic info (without investors/payments for performance)
        const drawdownInfoPromise = DrawdownService.getDrawdown(
          drawdownId,
          false, // don't include investors
          false, // don't include payments
          fundId
        );

        // Fetch available investors (new investors not in drawdown)
        const availableInvsPromise = DrawdownBatchService.getAvailableInvestors(
          fundId,
          drawdownId
        );

        // Fetch existing batches to get existing investors
        const batchesPromise = DrawdownBatchService.getBatches(
          fundId,
          drawdownId
        );

        const [drawdownData, availableInvs, batches] = await Promise.all([
          drawdownInfoPromise,
          availableInvsPromise,
          batchesPromise,
        ]);

        // Store drawdown info for the title
        setDrawdownInfo({
          purpose: drawdownData.drawdown.purpose,
          description: drawdownData.drawdown.description,
        });

        // Set available investors (all selected by default)
        setAvailableInvestors(
          availableInvs.map((inv) => ({
            ...inv,
            isSelected: true,
          }))
        );

        // Fetch batch previews to get investor details
        if (batches.length > 0) {
          const batchPreviewsPromises = batches.map((batch) =>
            DrawdownBatchService.getBatchPreview(fundId, drawdownId, batch.id)
          );

          const batchPreviews = await Promise.all(batchPreviewsPromises);

          // Extract existing investors from batch previews
          const existingInvs: ExistingInvestor[] = [];
          batchPreviews.forEach((preview) => {
            preview.investor_notices.forEach((notice) => {
              existingInvs.push({
                investor_id: notice.investor_id,
                investor_name: notice.investor_name,
                commitment_amount: notice.commitment_amount,
                drawdown_amount: notice.drawdown_amount,
                batch_name: preview.batch.batch_name,
              });
            });
          });

          setExistingInvestors(existingInvs);

          // Store the dates from the first batch for creating new batches
          if (batches[0]) {
            const noticeDate = batches[0].notice_date;
            if (noticeDate) {
              setDrawdownDates({
                issue_date: noticeDate,
                due_date: batches[0].due_date,
              });
            }
            setDueDate(batches[0].due_date);
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load investor data');
      } finally {
        setLoadingInvestors(false);
        setLoadingBatches(false);
      }
    };

    fetchData();
  }, [open, fundId, drawdownId]);

  return {
    loadingInvestors,
    loadingBatches,
    error,
    setError,
    availableInvestors,
    setAvailableInvestors,
    existingInvestors,
    drawdownDates,
    drawdownInfo,
    dueDate,
    setDueDate,
  };
}
